import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";

/**
 * 원곡 영상과 같이 흘러가는 악보 (2026-10, 새 디자인). 유튜브 공식 영상을 위에 작게 띄우고,
 * 영상 재생 위치(초)를 악보 진행(마디당 100)으로 바꿔 알려준다. 시작 위치(offset)는 원곡 비교로 찾은 값,
 * 어긋나면 ±0.5초 버튼으로 맞추고 곡마다 기억한다.
 */
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: any;
  }
}

let apiPromise: Promise<any> | null = null;
export const loadApi = () => {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (!apiPromise) {
    apiPromise = new Promise((resolve) => {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (prev) prev();
        resolve(window.YT);
      };
      const s = document.createElement("script");
      s.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(s);
    });
  }
  return apiPromise;
};

interface IVideoSync {
  musicId: number;
  youtubeId: string;
  offset: number; // 영상에서 악보 첫 마디가 시작하는 시각(초)
  barSec: number; // 한 마디 길이(초)
  barScales?: number[]; // 마디마다 길이 배율 (중간에 박자가 바뀐 마디)
  onProgress: (progress: number) => void; // 마디당 100 기준 진행
  onPlayingChange: (playing: boolean) => void;
  onClose: () => void;
}

const VideoSync: React.FC<IVideoSync> = ({ musicId, youtubeId, offset, barSec, barScales, onProgress, onPlayingChange, onClose }) => {
  const scalesRef = useRef<number[]>(barScales || []);
  scalesRef.current = barScales || [];
  const boxRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const key = `videoNudge:${musicId}`;
  const [nudge, setNudge] = useState<number>(() => {
    try {
      return Number(localStorage.getItem(key)) || 0;
    } catch (e) {
      return 0;
    }
  });
  const nudgeRef = useRef(nudge);
  nudgeRef.current = nudge;

  useEffect(() => {
    let timer: any;
    let alive = true;
    loadApi().then((YT) => {
      if (!alive || !boxRef.current) return;
      playerRef.current = new YT.Player(boxRef.current, {
        videoId: youtubeId,
        playerVars: { playsinline: 1, rel: 0, modestbranding: 1 },
        events: {
          onStateChange: (e: any) => onPlayingChange(e.data === YT.PlayerState.PLAYING),
        },
      });
      timer = setInterval(() => {
        const p = playerRef.current;
        if (!p || typeof p.getCurrentTime !== "function") return;
        if (p.getPlayerState && p.getPlayerState() !== YT.PlayerState.PLAYING) return;
        let t = p.getCurrentTime() - offset - nudgeRef.current;
        if (t <= 0) return onProgress(0);
        // 마디 길이가 다른 마디(2/4 등)를 지나며 지금 몇 번째 마디·칸인지 계산
        const sc = scalesRef.current;
        let bar = 0;
        while (bar < sc.length && t >= barSec * (sc[bar] || 1)) {
          t -= barSec * (sc[bar] || 1);
          bar++;
        }
        const d = barSec * (sc[bar] || 1);
        onProgress(bar * 100 + Math.floor((t / d) * 4) * 25);
      }, 150);
    });
    return () => {
      alive = false;
      clearInterval(timer);
      onPlayingChange(false);
      try {
        playerRef.current && playerRef.current.destroy();
      } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [youtubeId]);

  const change = (d: number) => {
    const v = Math.round((nudge + d) * 10) / 10;
    setNudge(v);
    try {
      localStorage.setItem(key, String(v));
    } catch (e) {}
  };

  return (
    <Wrap>
      <Frame>
        <div ref={boxRef} />
      </Frame>
      <Bar>
        <span>원곡 영상과 같이 흘러가요</span>
        <Sync>
          <button type="button" onClick={() => change(-0.5)} aria-label="악보를 0.5초 빠르게">악보 빠르게</button>
          <em>{nudge > 0 ? `+${nudge}` : nudge}초</em>
          <button type="button" onClick={() => change(0.5)} aria-label="악보를 0.5초 늦게">늦게</button>
        </Sync>
        <Close type="button" onClick={onClose} aria-label="영상 닫기">✕</Close>
      </Bar>
    </Wrap>
  );
};

export default VideoSync;

const Wrap = styled.div`
  position: sticky;
  top: 0;
  z-index: 50;
  width: 100%;
  margin-bottom: 14px;
  border-radius: 14px;
  overflow: hidden;
  background: #16181e;
  border: 1px solid #2a2e38;
`;

const Frame = styled.div`
  position: relative;
  width: 100%;
  padding-top: 42%;
  background: #000;
  & iframe,
  & > div {
    position: absolute;
    inset: 0;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  font-size: 12px;
  color: #a3a9b5;
  & > span {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Sync = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  & > button {
    height: 28px;
    padding: 0 8px;
    border-radius: 8px;
    border: 1px solid #2a2e38;
    background: none;
    color: #f3f4f6;
    font-size: 11px;
    cursor: pointer;
  }
  & > em {
    font-style: normal;
    min-width: 38px;
    text-align: center;
    color: #ffb547;
  }
`;

const Close = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 14px;
  border: 0;
  background: #2a2e38;
  color: #f3f4f6;
  cursor: pointer;
`;
